import React, { useState, useEffect, useRef } from "react";
import CustomPopup from "../../utils/CustomPopup";
import { useLocation } from "react-router-dom";
import { refreshToken } from "../../utils/apiClient";
import { isPublicRoute } from "../../config/routeConfig";

const COUNT_DOWN_TIME = 120;
const CONFIRMATION_TIMEOUT = COUNT_DOWN_TIME * 1000;
const INACTIVITY_TIMEOUT = 300000;
const TOKEN_REFRESH_BUFFER = 120000; // 2 minutes before expiration
// const INACTIVITY_TIMEOUT = 10000; // for test 10 seconds

const LogoutTimer = ({ logout, setIsAuthenticated }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [countdown, setCountdown] = useState(COUNT_DOWN_TIME);
  const location = useLocation();
  
  const timerIdRef = useRef(null);
  const confirmationTimerIdRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const tokenRefreshTimerRef = useRef(null);

  const handleLogout = () => {
    setIsAuthenticated(false);
    logout();
  };

  const handleTokenRefresh = async () => {
    try {
      console.log("Attempting to refresh token...");
      const response = await refreshToken();
      
      if (response.statusCode === "SUCCESS") {
        console.log("Token refreshed successfully. New expiry:", new Date(Date.now() + (response.response.expires_in * 1000)));
        // Setup next token refresh
        setupTokenRefreshTimer();
      } else {
        console.error("Token refresh failed:", response.message);
        // If refresh fails, proceed with logout
        handleLogout();
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      // If refresh fails, proceed with logout
      handleLogout();
    }
  };

  const setupTokenRefreshTimer = () => {
    // Clear existing token refresh timer
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }

    // Don't set up token refresh on public/unauthenticated routes
    if (isPublicRoute(location.pathname)) return;

    const tokenExpiresAt = sessionStorage.getItem("token_expires_at");
    if (!tokenExpiresAt) return;

    const expiryTime = parseInt(tokenExpiresAt);
    const currentTime = Date.now();
    const refreshTime = expiryTime - TOKEN_REFRESH_BUFFER; // 2 minutes before expiry

    const timeUntilRefresh = refreshTime - currentTime;

    if (timeUntilRefresh > 0) {
      console.log(`Token will be refreshed in ${Math.round(timeUntilRefresh / 1000)} seconds`);
      tokenRefreshTimerRef.current = setTimeout(handleTokenRefresh, timeUntilRefresh);
    } else {
      // Token is already expired or about to expire, refresh immediately
      handleTokenRefresh();
    }
  };

  const handleActivity = () => {
    resetTimer();
    if (showConfirmation) {
      clearTimeout(confirmationTimerIdRef.current);
      clearInterval(countdownIntervalRef.current);
      setShowConfirmation(false);
      setCountdown(COUNT_DOWN_TIME);
    }
  };

  const showConfirmationPopup = () => {
    // Don't show timer on public/unauthenticated routes
    if (isPublicRoute(location.pathname)) return;
  
    setShowConfirmation(true);
    setCountdown(COUNT_DOWN_TIME);
  
    clearTimeout(confirmationTimerIdRef.current);
    clearInterval(countdownIntervalRef.current);
  
    confirmationTimerIdRef.current = setTimeout(() => {
      handleLogout();
      setShowConfirmation(false);
    }, CONFIRMATION_TIMEOUT);
  
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const resetTimer = () => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }
    if (confirmationTimerIdRef.current) {
      clearTimeout(confirmationTimerIdRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setShowConfirmation(false);
    
    // Don't start timer on public/unauthenticated routes
    if (isPublicRoute(location.pathname)) return;
    
    timerIdRef.current = setTimeout(showConfirmationPopup, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    // Clear any existing timers when route changes
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }
    if (confirmationTimerIdRef.current) {
      clearTimeout(confirmationTimerIdRef.current);
    }
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setShowConfirmation(false);

    // Don't set up timers or event listeners on public/unauthenticated routes
    if (isPublicRoute(location.pathname)) {
      return;
    }

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keypress", handleActivity);

    resetTimer();
    
    // Setup token refresh timer on component mount
    setupTokenRefreshTimer();

    return () => {
      clearTimeout(timerIdRef.current);
      clearTimeout(confirmationTimerIdRef.current);
      clearTimeout(tokenRefreshTimerRef.current);
      clearInterval(countdownIntervalRef.current);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keypress", handleActivity);
    };
  }, [logout, setIsAuthenticated, location.pathname]);

  // ============================================
  // TEMPORARY DEBUGGING USEEFFECT - FOR TESTING
  // ============================================
  // useEffect(() => {
  //   const debugInterval = setInterval(() => {
  //     const currentTime = Date.now();
  //     const tokenExpiresAt = sessionStorage.getItem("token_expires_at");
      
  //     // Calculate inactivity popup trigger time (assuming timer was just reset)
  //     const inactivityTriggerTime = currentTime + INACTIVITY_TIMEOUT;
  //     const inactivityTimeRemaining = INACTIVITY_TIMEOUT;
      
  //     // Calculate token refresh trigger time
  //     let tokenRefreshTriggerTime = null;
  //     let tokenRefreshTimeRemaining = null;
  //     let tokenExpiryTime = null;
      
  //     if (tokenExpiresAt) {
  //       tokenExpiryTime = parseInt(tokenExpiresAt);
  //       tokenRefreshTriggerTime = tokenExpiryTime - TOKEN_REFRESH_BUFFER;
  //       tokenRefreshTimeRemaining = tokenRefreshTriggerTime - currentTime;
  //     }

  //     console.log("sessionManagementDebugger", "=== SESSION MANAGEMENT DEBUG INFO ===");
  //     console.log("sessionManagementDebugger", `Current time: ${new Date(currentTime).toLocaleTimeString()}`);
  //     console.log("sessionManagementDebugger", "");
      
  //     // Inactivity popup info
  //     console.log("sessionManagementDebugger", "🔴 INACTIVITY POPUP:");
  //     console.log("sessionManagementDebugger", `  - Will trigger in: ${Math.round(inactivityTimeRemaining / 1000)} seconds`);
  //     console.log("sessionManagementDebugger", `  - Will trigger at: ${new Date(inactivityTriggerTime).toLocaleTimeString()}`);
  //     console.log("sessionManagementDebugger", `  - Timeout duration: ${INACTIVITY_TIMEOUT / 1000} seconds (${INACTIVITY_TIMEOUT / 60000} minutes)`);
  //     console.log("sessionManagementDebugger", "");
      
  //     // Token refresh info
  //     if (tokenExpiresAt) {
  //       console.log("sessionManagementDebugger", "🔄 TOKEN REFRESH:");
  //       console.log("sessionManagementDebugger", `  - Token expires at: ${new Date(tokenExpiryTime).toLocaleTimeString()}`);
  //       console.log("sessionManagementDebugger", `  - Refresh buffer: ${TOKEN_REFRESH_BUFFER / 1000} seconds (${TOKEN_REFRESH_BUFFER / 60000} minutes)`);
        
  //       if (tokenRefreshTimeRemaining > 0) {
  //         console.log("sessionManagementDebugger", `  - Will refresh in: ${Math.round(tokenRefreshTimeRemaining / 1000)} seconds`);
  //         console.log("sessionManagementDebugger", `  - Will refresh at: ${new Date(tokenRefreshTriggerTime).toLocaleTimeString()}`);
  //       } else {
  //         console.log("sessionManagementDebugger", `  - ⚠️  SHOULD REFRESH NOW! (${Math.round(Math.abs(tokenRefreshTimeRemaining) / 1000)} seconds overdue)`);
  //       }
  //     } else {
  //       console.log("sessionManagementDebugger", "🔄 TOKEN REFRESH:");
  //       console.log("sessionManagementDebugger", `  - ❌ No token expiry time found in sessionStorage`);
  //     }
      
  //     console.log("sessionManagementDebugger", "");
  //     console.log("sessionManagementDebugger", "📊 TIMER STATUS:");
  //     console.log("sessionManagementDebugger", `  - Inactivity timer active: ${timerIdRef.current ? 'YES' : 'NO'}`);
  //     console.log("sessionManagementDebugger", `  - Token refresh timer active: ${tokenRefreshTimerRef.current ? 'YES' : 'NO'}`);
  //     console.log("sessionManagementDebugger", `  - Confirmation popup showing: ${showConfirmation ? 'YES' : 'NO'}`);
  //     console.log("sessionManagementDebugger", `  - Current page: ${location.pathname}`);
  //     console.log("sessionManagementDebugger", "==========================================");
  //     console.log("sessionManagementDebugger", "");
      
  //   }, 10000); // Log every 10 seconds

  //   // Initial log
  //   console.log("sessionManagementDebugger", "🚀 Session Management Debugging Started - Logging every 10 seconds");
    
  //   return () => {
  //     clearInterval(debugInterval);
  //     console.log("sessionManagementDebugger", "🛑 Session Management Debugging Stopped");
  //   };
  // }, [showConfirmation, location.pathname]);

  const handleStayLoggedIn = () => {
    setShowConfirmation(false);
    clearTimeout(confirmationTimerIdRef.current);
    resetTimer();
  };

  return showConfirmation ? (
    <CustomPopup
      content={
        <div>
          <span>
            {`Are you still using the application? If yes, move your mouse or
            press any key, else you will be logged out in ${countdown} seconds.`}
          </span>
          <div>
            {/* <Button
              label={"Stay Logged In"}
              onClick={handleStayLoggedIn}
              backgroundColor={"#ff4e3a"}
              width={"25vh"}
              style={{ borderRadius: "1vh", margin: "1vh" }}
              height={"4vh"}
            />
            <Button
              label={"Logout"}
              onClick={handleLogoutConfirmed}
              backgroundColor={"#ff4e3a"}
              width={"25vh"}
              style={{ borderRadius: "1vh", margin: "1vh" }}
              height={"4vh"}
            /> */}
          </div>
        </div>
      }
      visible={true}
      setVisible={() => setShowConfirmation(false)}
    />
  ) : null;
};

export default LogoutTimer;
